/**
 * @file CoachingIndividuelObjectifs.tsx
 * @summary Article de blog — CoachingIndividuelObjectifs
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, AdaptationChistera, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/coaching-individuel-objectifs-sportifs";
const TITLE = "Coaching individuel selon vos objectifs : force, perte de poids, endurance, blessure";
const DESC = "Le coaching individuel CrossFit Chistera s'adapte à votre objectif : prise de force, perte de poids durable, endurance, retour de blessure. Programmation sur mesure, suivi hebdo.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Faut-il déjà être adhérent pour faire du coaching individuel ?", a: "Non. Le coaching individuel est ouvert aux non-adhérents. Beaucoup de personnes commencent par là, notamment quand elles veulent un cadre très ciblé avant de basculer en classe." },
  { q: "Combien de séances par semaine ?", a: "1 à 4 selon l'objectif et le budget. Pour la plupart des objectifs (perte de poids, force, endurance), 2 séances individuelles + 1 à 2 WOD de groupe par semaine est un excellent équilibre." },
  { q: "Vous faites de la nutrition ?", a: "On donne des repères simples (apport protéique, structure des repas, hydratation), mais on ne se substitue pas à un diététicien. Si l'objectif est principalement nutritionnel, on oriente vers un partenaire qualifié." },
  { q: "Coaching individuel vs petit groupe ?", a: "L'individuel offre une attention 100 % dédiée et une programmation sur mesure. Le petit groupe (2-3 personnes) coûte moins cher et garde l'émulation, mais la programmation est moins fine." },
  { q: "Et si je voyage souvent pour le boulot ?", a: "On adapte. Programmation envoyée à la semaine, séances réalisables en salle d'hôtel ou en extérieur quand vous êtes en déplacement. C'est un cas fréquent chez nos cadres en mission." },
];

export default function CoachingIndividuelObjectifs() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Coaching individuel selon vos objectifs sportifs"
      breadcrumbLabel="Coaching individuel objectifs"
      category="Coaching · Méthode"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-03" readingMinutes={9}
      ctaVariant="default"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-03", author, reviewer, faq: faq.map(f => ({ q: f.q, a: typeof f.a === "string" ? f.a : "" })) })}
    >
      <EnBref>
        <p>Le coaching individuel n'est pas réservé aux compétiteurs. Il sert quand vous avez <strong>un objectif précis</strong> (perdre 8 kg durablement, passer un strict pull-up, courir 10 km sans casser, revenir d'une opération) et que les WOD de groupe seuls ne suffisent pas à <strong>cadrer la progression</strong>.</p>
      </EnBref>

      <h2>4 objectifs typiques et comment on les structure</h2>

      <h3>Objectif 1 — Prise de force</h3>
      <p>Programmation type <strong>5/3/1</strong>, <strong>conjugate</strong> ou <strong>linéaire</strong> selon votre niveau. Cycles de 4 à 12 semaines avec tests de force aux bornes. On travaille les 4 mouvements clés (squat, bench/press, deadlift, OHP) avec assistance ciblée. Idéal pour les athlètes qui plafonnent en force malgré un volume de WOD régulier.</p>

      <h3>Objectif 2 — Perte de poids durable</h3>
      <p>L'erreur classique : empiler les WOD high-intensity en mangeant moins. Résultat : perte de muscle, fatigue, reprise de poids. Notre approche : <strong>préserver la masse musculaire</strong> (force 2x/semaine), ajouter du <strong>cardio basse intensité</strong> (zone 2, 30-45 min) et caler les apports avec un pro de la nutrition si besoin. Objectif : 0,5 à 1 kg par semaine, durable.</p>

      <h3>Objectif 3 — Endurance / hybrid</h3>
      <p>Pour ceux qui veulent courir, faire un trail ou un HYROX <strong>sans perdre leur force</strong>. Mix entre séances aérobies longues (zone 2), intervalles VMA, et maintien de la base force. Programmation calée sur votre événement cible.</p>

      <h3>Objectif 4 — Retour de blessure ou d'opération</h3>
      <p>Voir aussi notre <Link to="/blog/reprendre-le-sport-apres-blessure">guide en 4 phases</Link>. Le coaching individuel est <strong>quasi-obligatoire</strong> sur les 6 à 12 premières semaines : on adapte chaque exercice à votre stade de cicatrisation, en lien avec votre kiné.</p>

      <AdaptationChistera title="Le déroulé chez Chistera">
        <ol>
          <li><strong>Échange découverte gratuit</strong> — vos objectifs, votre histoire sportive, vos contraintes.</li>
          <li><strong>Bilan initial</strong> (1h) — tests physiques adaptés à l'objectif (force, cardio, mobilité).</li>
          <li><strong>Plan personnalisé</strong> sur 8 à 16 semaines avec jalons mesurables.</li>
          <li><strong>Séances</strong> en salle ou à distance (programmation envoyée), avec coach référent.</li>
          <li><strong>Suivi hebdo</strong> et ajustements selon votre semaine réelle (sommeil, stress, voyage).</li>
        </ol>
      </AdaptationChistera>

      <h2>Combien ça coûte vraiment ?</h2>
      <p>Le coaching individuel est plus cher qu'un abonnement classe (logique : un coach pour vous seul). En contrepartie, le ratio progression/temps investi est <strong>nettement supérieur</strong>. Pour la plupart des objectifs, un cycle de 8 à 12 semaines suffit ensuite à basculer en autonomie sur des classes de groupe.</p>
      <p>Tarifs détaillés sur devis lors de l'échange découverte — la fourchette dépend du nombre de séances individuelles et de la durée du suivi.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Coaching individuel pour HYROX, ATHX, CrossFit", to: "/blog/coaching-individuel-competition-hyrox-athx-crossfit" },
        { label: "Reprendre le sport après une blessure", to: "/blog/reprendre-le-sport-apres-blessure" },
        { label: "Sport après 35 ans : cadre actif", to: "/blog/sport-apres-35-ans-cadre-actif" },
      ]} />

      <SourcesSection items={[
        { label: "ACSM — Resistance Training for Health (Position Stand)", href: "https://journals.lww.com/acsm-msse/Fulltext/2009/03000/Progression_Models_in_Resistance_Training_for.26.aspx" },
        { label: "Hall KD et al. — Energy balance and its components: implications for body weight regulation. Am J Clin Nutr, 2012", href: "https://pubmed.ncbi.nlm.nih.gov/22258266/" },
      ]} />
    </ArticleLayout>
  );
}
